import React, { useEffect } from 'react';
import { isValid, SelectedImage } from '../../FileUpload/utils/validateImage';
import { createFormElement } from '../../../components/molecules/Form/FormFactory';
import FormRadioGroup, {
  FormRadioGroupOrientation,
} from '../../../components/molecules/Form/components/FormRadioGroup/FormRadioGroup';
import FormsRow from '../../../components/molecules/Form/components/FormsRow/FormsRow';
import FormCheckbox from '../../../components/molecules/Form/components/FormCheckbox/FormCheckbox';
import { FormUtils, useFormContext } from '../../../components/molecules/Form';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { RequiredHelpPhrase } from '../../Form2';

const normalizedRadios = (radioInputs: any) => {
  return radioInputs
    .sort((element1: any, element2: any) => element1.value.localeCompare(element2.value));
};

type ImageUploadParamsProps = {
  id: string;
  selectedPreview: SelectedImage | undefined;
  hasInitialImage?: boolean;
};
export function ImageUploadParams({ selectedPreview, hasInitialImage, id }: ImageUploadParamsProps) {
  const { t } = useSonTranslation('ImageUpload');
  const { inputs } = useFormContext();
  const { imageuploadparams } = inputs as any;
  const { image, alt, caption, size, popUp, copyright } = imageuploadparams.params;
  useEffect(() => {
    if (size && isValid(selectedPreview)) {
      const newSize = normalizedRadios(size.radioInputs).find(
        (radio: any) => !radio.disabled,
      )?.value;
      imageuploadparams.onChange(size.name, newSize);
    }
  }, [image, imageuploadparams, selectedPreview, size]);

  if (!(isValid(selectedPreview) || hasInitialImage)) {
    return null;
  }

  return (
    <>
      {(alt || copyright) && <RequiredHelpPhrase />}
      {alt &&
        createFormElement({
          labelHelpText: t('pictureDescriptionHelpText'),
        })(alt, id, imageuploadparams.onChange, t('pictureDescriptionLabel'))}
      {caption && createFormElement()(caption, id, imageuploadparams.onChange, t('pictureCaptionLabel'))}
      {size && size.radioInputs.length > 0 && (
        <FormRadioGroup
          id={id}
          legend={t('pictureSizeLabel')}
          helpText={t('pictureSizeHelpText')}
          orientation={FormRadioGroupOrientation.horizontal}
          radios={normalizedRadios(size.radioInputs)}
          labels={size.radioInputs.reduce(
            (acc: Record<'small' | 'medium' | 'large', string>, element: { value: 'small' | 'medium' | 'large' }) => {
              acc[element.value] = t(`pictureSizeOptions.${element.value}`);
              return acc;
            },
            {} as Record<'small' | 'medium' | 'large', string>,
          )}
          selected={size.value}
          onChange={imageuploadparams.onChange}
        />
      )}
      {popUp && (
        <FormsRow>
          <FormCheckbox
            id={id}
            labelTitle={t('popUp')}
            name={popUp.name}
            layout="switch"
            checked={popUp.checked}
            value={popUp.value}
            onChange={popUp.onChange}
            labelHelpText={t('popUpHelpText')}
          />
        </FormsRow>
      )}
      {copyright &&
        createFormElement({
          labelHelpText: t('pictureCopyrightHelpText'),
        })(copyright, id, imageuploadparams.onChange, t('pictureCopyrightLabel'))}
      {FormUtils.renderHiddenFields(imageuploadparams.params)}
    </>
  );
}
